import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ContactRound, Filter, Plus, Search, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const PAGE_SIZE = 25;
const ANY = "__any__";

const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? "" : v)}>
    <SelectTrigger className="h-9 w-full sm:w-[150px] bg-white text-xs" data-testid={testId}>
      <SelectValue placeholder={label} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={ANY}>{label}: All</SelectItem>
      {(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function CandidatesList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const basePath = user.role === "recruiter" ? "/recruiter/candidates" : "/admin/candidates";

  const [data, setData] = useState({ total: 0, items: [] });
  const [master, setMaster] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [mine, setMine] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ full_name: "", mobile: "", alt_mobile: "", email: "", current_city: "", source: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/master-data").then((res) => setMaster(res.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { q: q || undefined, skip: page * PAGE_SIZE, limit: PAGE_SIZE, mine: mine || undefined };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/candidates", { params })
      .then((res) => setData(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [q, page, filters, mine]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const setFilter = (key, value) => {
    setPage(0);
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const activeFilterCount = useMemo(() => Object.values(filters).filter(Boolean).length, [filters]);

  const addCandidate = async () => {
    if (!addForm.full_name.trim() || !/^[6-9]\d{9}$/.test(addForm.mobile)) {
      toast.error("Enter a valid name and 10-digit mobile number");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...addForm, alt_mobile: addForm.alt_mobile || null, email: addForm.email || null, current_city: addForm.current_city || null, source: addForm.source || null };
      const res = await api.post("/candidates", payload);
      toast.success(`Candidate ${res.data.code} created`);
      setAddOpen(false);
      navigate(`${basePath}/${res.data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.candidate_id) {
        const ownerLine = detail.owner_name
          ? `Currently with ${detail.owner_name}${detail.owner_mobile ? ` · ${detail.owner_mobile}` : ""}`
          : "Currently unassigned (in the pool)";
        toast.info(detail.message || "Candidate already exists — opening existing profile", {
          description: ownerLine,
          duration: 8000,
        });
        setAddOpen(false);
        navigate(`${basePath}/${detail.candidate_id}`);
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="space-y-6" data-testid="candidates-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Candidate Master</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Single source of truth — {data.total} candidate{data.total === 1 ? "" : "s"}. One profile per person, never deleted.
          </p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-lg" data-testid="candidate-add-button">
              <Plus className="h-4 w-4 mr-1.5" /> Add Candidate
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="candidate-add-dialog">
            <DialogHeader><DialogTitle>Add candidate</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Full name *</Label>
                <Input data-testid="candidate-add-name" value={addForm.full_name} onChange={(e) => setAddForm({ ...addForm, full_name: e.target.value })} placeholder="e.g. Rahul Sharma" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Mobile *</Label>
                  <Input data-testid="candidate-add-mobile" value={addForm.mobile} maxLength={10} onChange={(e) => setAddForm({ ...addForm, mobile: e.target.value.replace(/\D/g, "") })} placeholder="10-digit mobile" />
                </div>
                <div className="space-y-1.5">
                  <Label>Alternate mobile</Label>
                  <Input data-testid="candidate-add-alt-mobile" value={addForm.alt_mobile} maxLength={10} onChange={(e) => setAddForm({ ...addForm, alt_mobile: e.target.value.replace(/\D/g, "") })} placeholder="Optional" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input data-testid="candidate-add-email" type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} placeholder="Optional" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>City</Label>
                  <Select value={addForm.current_city || ANY} onValueChange={(v) => setAddForm({ ...addForm, current_city: v === ANY ? "" : v })}>
                    <SelectTrigger data-testid="candidate-add-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ANY}>Not set</SelectItem>
                      {(master.city || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Source</Label>
                  <Select value={addForm.source || ANY} onValueChange={(v) => setAddForm({ ...addForm, source: v === ANY ? "" : v })}>
                    <SelectTrigger data-testid="candidate-add-source"><SelectValue placeholder="Select source" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ANY}>Not set</SelectItem>
                      {(master.source || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Duplicates are detected automatically by mobile, alternate mobile and email — if the candidate exists, their profile opens instead.</p>
              <Button onClick={addCandidate} disabled={busy} className="w-full rounded-lg" data-testid="candidate-add-submit">
                {busy ? "Checking..." : "Create candidate"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="candidates-search-input" placeholder="Search ID, name or mobile..." className="pl-9 bg-white h-9" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} />
        </div>
        <FilterSelect label="City" value={filters.city} onChange={(v) => setFilter("city", v)} options={master.city} testId="candidates-filter-city" />
        <FilterSelect label="Job Role" value={filters.job_role} onChange={(v) => setFilter("job_role", v)} options={master.job_role} testId="candidates-filter-job-role" />
        <FilterSelect label="Experience" value={filters.experience} onChange={(v) => setFilter("experience", v)} options={master.experience} testId="candidates-filter-experience" />
        <FilterSelect label="Employment" value={filters.employment_status} onChange={(v) => setFilter("employment_status", v)} options={master.employment_status} testId="candidates-filter-employment" />
        <Select value={filters.resume_available || ANY} onValueChange={(v) => setFilter("resume_available", v === ANY ? "" : v)}>
          <SelectTrigger className="h-9 w-[130px] bg-white text-xs" data-testid="candidates-filter-resume">
            <SelectValue placeholder="Resume" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Resume: All</SelectItem>
            <SelectItem value="true">Resume: Yes</SelectItem>
            <SelectItem value="false">Resume: No</SelectItem>
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 rounded-lg" data-testid="candidates-more-filters">
              <Filter className="h-3.5 w-3.5 mr-1.5" /> More{activeFilterCount > 0 && ` (${activeFilterCount})`}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 space-y-2.5" align="end" data-testid="candidates-more-filters-panel">
            <p className="text-sm font-medium">More filters</p>
            <div className="grid grid-cols-2 gap-2">
              <FilterSelect label="State" value={filters.state} onChange={(v) => setFilter("state", v)} options={master.state} testId="candidates-filter-state" />
              <FilterSelect label="Industry" value={filters.industry} onChange={(v) => setFilter("industry", v)} options={master.industry} testId="candidates-filter-industry" />
              <FilterSelect label="Qualification" value={filters.qualification} onChange={(v) => setFilter("qualification", v)} options={master.qualification} testId="candidates-filter-qualification" />
              <FilterSelect label="Skill" value={filters.skill} onChange={(v) => setFilter("skill", v)} options={master.skill} testId="candidates-filter-skill" />
              <FilterSelect label="Language" value={filters.language} onChange={(v) => setFilter("language", v)} options={master.language} testId="candidates-filter-language" />
              <FilterSelect label="Source" value={filters.source} onChange={(v) => setFilter("source", v)} options={master.source} testId="candidates-filter-source" />
              <FilterSelect label="Pref. Location" value={filters.preferred_location} onChange={(v) => setFilter("preferred_location", v)} options={master.preferred_location} testId="candidates-filter-pref-location" />
              <FilterSelect label="Status" value={filters.candidate_status} onChange={(v) => setFilter("candidate_status", v)} options={["Active", "Inactive"]} testId="candidates-filter-status" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Expected salary min</Label>
                <Input type="number" className="h-8" data-testid="candidates-filter-salary-min" value={filters.salary_min || ""} onChange={(e) => setFilter("salary_min", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Expected salary max</Label>
                <Input type="number" className="h-8" data-testid="candidates-filter-salary-max" value={filters.salary_max || ""} onChange={(e) => setFilter("salary_max", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Created from</Label>
                <Input type="date" className="h-8" data-testid="candidates-filter-created-from" value={filters.created_from || ""} onChange={(e) => setFilter("created_from", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Created to</Label>
                <Input type="date" className="h-8" data-testid="candidates-filter-created-to" value={filters.created_to || ""} onChange={(e) => setFilter("created_to", e.target.value)} />
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {user.role === "recruiter" && (
          <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground cursor-pointer">
            <Switch checked={mine} onCheckedChange={(v) => { setMine(v); setPage(0); }} data-testid="candidates-mine-toggle" />
            My candidates
          </label>
        )}

        {(activeFilterCount > 0 || q) && (
          <Button variant="ghost" size="sm" className="h-9 text-muted-foreground" data-testid="candidates-clear-filters"
            onClick={() => { setFilters({}); setQ(""); setPage(0); }}>
            <X className="h-3.5 w-3.5 mr-1" /> Clear
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
      ) : data.items.length === 0 ? (
        <Card className="rounded-xl border-border shadow-none">
          <EmptyState icon={ContactRound} title="No candidates found" description="Try adjusting your search or filters, or add a new candidate." testId="candidates-empty" />
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <Card className="rounded-xl border-border shadow-none overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <Table data-testid="candidates-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Job Role</TableHead>
                    <TableHead>Experience</TableHead>
                    <TableHead>Exp. Salary</TableHead>
                    <TableHead>Employment</TableHead>
                    <TableHead>Last Contact</TableHead>
                    <TableHead>Resume</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((c) => (
                    <TableRow key={c.id} className="hover:bg-muted/60" data-testid="candidate-row">
                      <TableCell className="font-mono text-xs font-medium">{c.code}</TableCell>
                      <TableCell>
                        <span className="font-medium text-sm">{c.full_name || "—"}</span>
                        <span className="block text-[10px] text-muted-foreground">
                          {c.blacklisted && <Badge variant="outline" className="rounded-full text-[9px] px-1.5 py-0 mr-1 bg-red-50 text-red-700 border-red-200">Blacklisted</Badge>}
                          {c.do_not_contact && <Badge variant="outline" className="rounded-full text-[9px] px-1.5 py-0 mr-1 bg-amber-50 text-amber-700 border-amber-200">Do Not Contact</Badge>}
                          {c.owner_name && <span>Working: {c.owner_name}</span>}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{c.mobile}</TableCell>
                      <TableCell className="text-sm">{c.current_city || "—"}</TableCell>
                      <TableCell className="text-sm">{c.job_role || "—"}</TableCell>
                      <TableCell className="text-sm">{c.total_experience || "—"}</TableCell>
                      <TableCell className="text-sm">{c.expected_salary ? `₹${Number(c.expected_salary).toLocaleString("en-IN")}` : "—"}</TableCell>
                      <TableCell className="text-sm">{c.employment_status || "—"}</TableCell>
                      <TableCell className="text-sm">{formatDate(c.last_contact_date)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`rounded-full text-[10px] ${c.resume_available ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-muted text-muted-foreground"}`}>
                          {c.resume_available ? "Yes" : "No"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild variant="outline" size="sm" className="h-8 rounded-lg text-xs" data-testid="candidate-view-profile-button">
                          <Link to={`${basePath}/${c.id}`}>View Complete Profile</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {data.items.map((c) => (
              <Card key={c.id} className="rounded-xl border-border shadow-none p-4" data-testid="candidate-card-mobile">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-mono text-[10px] text-muted-foreground">{c.code}</p>
                    <p className="font-medium">{c.full_name || "—"}</p>
                    <p className="text-xs text-muted-foreground font-mono">{c.mobile}</p>
                  </div>
                  <Badge variant="outline" className={`rounded-full text-[10px] ${c.resume_available ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-muted text-muted-foreground"}`}>
                    Resume: {c.resume_available ? "Yes" : "No"}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-3 text-xs text-muted-foreground">
                  <span>City: <span className="text-foreground">{c.current_city || "—"}</span></span>
                  <span>Role: <span className="text-foreground">{c.job_role || "—"}</span></span>
                  <span>Exp: <span className="text-foreground">{c.total_experience || "—"}</span></span>
                  <span>Salary: <span className="text-foreground">{c.expected_salary ? `₹${Number(c.expected_salary).toLocaleString("en-IN")}` : "—"}</span></span>
                </div>
                <Button asChild variant="outline" size="sm" className="w-full mt-3 rounded-lg text-xs">
                  <Link to={`${basePath}/${c.id}`}>View Complete Profile</Link>
                </Button>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Page {page + 1} of {totalPages} · {data.total} candidates</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-lg" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="candidates-prev-page">Previous</Button>
              <Button variant="outline" size="sm" className="rounded-lg" disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} data-testid="candidates-next-page">Next</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
